import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Badge } from '@cloudscape-design/components';
import PdfHighlightViewer from './PdfHighlightViewer';

/**
 * CitationSidePanel - A slide-in panel that hosts the PDF viewer alongside research results.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 6.2, 6.3, 8.2, 8.3, 8.7
 */
function CitationSidePanel({
  isOpen,
  documentName,
  documentPath,
  pageNumber,
  highlightText,
  citationText,
  onClose,
}) {
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [announcement, setAnnouncement] = useState('');
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [swipeCurrentX, setSwipeCurrentX] = useState(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Announce open/close state changes via aria-live
  useEffect(() => {
    if (isOpen) {
      setAnnouncement('Citation panel opened');
      setIsAnimatingOut(false);
    } else {
      setAnnouncement('Citation panel closed');
    }
  }, [isOpen]);

  // Escape key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trapping when open
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const panel = panelRef.current;

    // Focus the close button when panel opens
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    const handleTabTrap = (e) => {
      if (e.key !== 'Tab') return;

      const focusableSelectors =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableElements = panel.querySelectorAll(focusableSelectors);
      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first element, wrap to last
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabTrap);
    return () => document.removeEventListener('keydown', handleTabTrap);
  }, [isOpen]);

  // Click-outside / tap-outside detection
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideInteraction = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
    };
  }, [isOpen, onClose]);

  // Swipe-to-dismiss gesture handlers
  const handleTouchStart = useCallback((e) => {
    setSwipeStartX(e.touches[0].clientX);
    setSwipeCurrentX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (swipeStartX === null) return;
    setSwipeCurrentX(e.touches[0].clientX);
  }, [swipeStartX]);

  const handleTouchEnd = useCallback(() => {
    if (swipeStartX !== null && swipeCurrentX !== null) {
      const swipeDistance = swipeCurrentX - swipeStartX;
      if (swipeDistance > 100) {
        onClose();
      }
    }
    setSwipeStartX(null);
    setSwipeCurrentX(null);
  }, [swipeStartX, swipeCurrentX, onClose]);

  // Calculate swipe offset for visual feedback
  const swipeOffset =
    swipeStartX !== null && swipeCurrentX !== null
      ? Math.max(0, swipeCurrentX - swipeStartX)
      : 0;

  // Don't render if not open and not animating out
  if (!isOpen && !isAnimatingOut) {
    return (
      <div aria-live="polite" style={styles.srOnly}>
        {announcement}
      </div>
    );
  }

  return (
    <>
      {/* Aria-live region for open/close announcements */}
      <div aria-live="polite" style={styles.srOnly}>
        {announcement}
      </div>

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Citation panel for ${documentName}`}
        style={{
          ...styles.panel,
          transform: isOpen
            ? `translateX(${swipeOffset}px)`
            : 'translateX(100%)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <span style={styles.documentName} title={documentName}>
              View {documentName}
            </span>
            <Badge color="blue">Page {pageNumber}</Badge>
          </div>
          <div style={styles.legend}>
            <span style={styles.legendItem}>
              <span style={styles.legendSwatchPink} />
              Cited text
            </span>
            <span style={styles.legendItem}>
              <span style={styles.legendSwatchYellow} />
              Source passage
            </span>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close citation panel"
            style={styles.closeButton}
          >
            ✕
          </button>
        </div>

        {/* Panel body — PDF viewer */}
        <div style={styles.body}>
          <PdfHighlightViewer
            documentPath={documentPath}
            pageNumber={pageNumber}
            highlightText={highlightText}
            citationText={citationText}
            documentName={documentName}
            onError={(err) => console.error('PDF load error:', err)}
          />
        </div>
      </div>
    </>
  );
}

const styles = {
  panel: {
    position: 'fixed',
    right: 0,
    top: 0,
    height: '100vh',
    width: '50vw',
    minWidth: '400px',
    background: '#ffffff',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #e9ebed',
    backgroundColor: '#fafafa',
    flexShrink: 0,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
  },
  documentName: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#16191f',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  closeButton: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: '1px solid #d5dbdb',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#545b64',
    flexShrink: 0,
    marginLeft: '12px',
  },
  legend: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: 'auto',
    marginRight: '8px',
    fontSize: '11px',
    color: '#687078',
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
  },
  legendSwatchPink: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    backgroundColor: 'rgba(233, 30, 99, 0.35)',
    border: '1px solid rgba(233, 30, 99, 0.6)',
    borderRadius: '2px',
  },
  legendSwatchYellow: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    backgroundColor: 'rgba(255, 235, 59, 0.3)',
    border: '1px solid rgba(255, 193, 7, 0.6)',
    borderRadius: '2px',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  srOnly: {
    position: 'absolute',
    left: '-9999px',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
  },
};

export default CitationSidePanel;
